/**
 * Create Airline Screen
 * Displayed when user needs to create an airline
 * No Supabase - saves directly to localStorage
 */

import React, { useState } from 'react'
import { useAuth } from './AuthContext'
import { debug } from '../../lib/debug.js'

export default function CreateAirlineScreen() {
  const { onAirlineCreated, signOut } = useAuth()

  const [step, setStep] = useState(1) // 1: name, 2: callsign, 3: confirm
  const [name, setName] = useState('')
  const [callsign, setCallsign] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Check if we have an existing airline in localStorage
  const tryRecoverAirline = () => {
    const saved = localStorage.getItem('playerAirline')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        debug('[CreateAirline] Recovered existing airline:', parsed)
        onAirlineCreated({
          name: parsed.name,
          callsign: parsed.iataCode,
          hub_code: parsed.hub,
        })
      } catch (e) {
        setError('Could not recover airline data')
      }
    } else {
      setError('No existing airline found - please create one below')
    }
  }

  const handleCreateAirline = async () => {
    setLoading(true)
    setError(null)

    try {
      // Save airline to localStorage
      const airlineData = {
        name,
        iataCode: callsign,
        hub: 'KPDX',
        colors: { primary: '#60ffd0', secondary: '#1a1a2e' },
      }

      localStorage.setItem('playerAirline', JSON.stringify(airlineData))
      debug('[CreateAirline] Saved airline to localStorage:', airlineData)

      onAirlineCreated({
        name,
        callsign,
        hub_code: 'KPDX',
      })
    } catch (err) {
      console.error('Create airline error:', err)
      setError(err.message || 'Failed to create airline')
    }

    setLoading(false)
  }

  const validateName = () => {
    if (name.length < 3) {
      setError('Airline name must be at least 3 characters')
      return false
    }
    if (name.length > 50) {
      setError('Airline name must be 50 characters or less')
      return false
    }
    return true
  }

  const validateCallsign = () => {
    const cleaned = callsign.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (cleaned.length < 2) {
      setError('Callsign must be at least 2 characters')
      return false
    }
    if (cleaned.length > 6) {
      setError('Callsign must be 6 characters or less')
      return false
    }
    setCallsign(cleaned)
    return true
  }

  const nextStep = () => {
    setError(null)
    if (step === 1 && validateName()) {
      setStep(2)
    } else if (step === 2 && validateCallsign()) {
      setStep(3)
    }
  }

  const prevStep = () => {
    setError(null)
    setStep(Math.max(1, step - 1))
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)' }}>

      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Create Your Airline
          </h1>
          <p className="text-gray-400">
            Step {step} of 3
          </p>
        </div>

        {/* Progress Bar */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full transition-colors ${
                s <= step ? 'bg-[#60ffd0]' : 'bg-[#333]'
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="bg-[#111] border border-[#222] rounded-lg p-8 shadow-xl">
          {/* Error */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-800 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Airline Name */}
          {step === 1 && (
            <div>
              <label className="block text-lg text-white mb-2">
                What's your airline called?
              </label>
              <p className="text-gray-500 text-sm mb-4">
                Choose a name that will strike fear into your competitors
              </p>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#333] rounded-lg
                  text-white text-lg placeholder-gray-600 focus:outline-none focus:border-[#60ffd0]
                  transition-colors"
                placeholder="Pacific Wings Airlines"
                autoFocus
              />
              <p className="text-gray-600 text-xs mt-2">
                3-50 characters
              </p>
            </div>
          )}

          {/* Step 2: Callsign */}
          {step === 2 && (
            <div>
              <label className="block text-lg text-white mb-2">
                What's your radio callsign?
              </label>
              <p className="text-gray-500 text-sm mb-4">
                This is how ATC will identify your flights
              </p>
              <input
                type="text"
                value={callsign}
                onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#333] rounded-lg
                  text-white text-lg placeholder-gray-600 focus:outline-none focus:border-[#60ffd0]
                  transition-colors font-mono tracking-wider"
                placeholder="PACWING"
                maxLength={6}
                autoFocus
              />
              <p className="text-gray-600 text-xs mt-2">
                2-6 alphanumeric characters (e.g., DELTA, UAL, AAL)
              </p>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div>
              <label className="block text-lg text-white mb-4">
                Ready to launch?
              </label>

              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500">Airline Name</span>
                  <span className="text-white font-medium">{name}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500">Callsign</span>
                  <span className="text-white font-mono">{callsign}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500">Hub</span>
                  <span className="text-white">Portland (KPDX)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Starting Cash</span>
                  <span className="text-[#60ffd0] font-medium">$40,000,000</span>
                </div>
              </div>

              <div className="bg-[#0d1f1a] border border-[#1a3a2f] rounded-lg p-4">
                <h4 className="text-[#60ffd0] font-medium mb-2">You'll start with:</h4>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li>- 4 starter routes from Portland</li>
                  <li>- 8 captains and 10 first officers</li>
                  <li>- $40M to build your fleet</li>
                </ul>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                onClick={prevStep}
                disabled={loading}
                className="flex-1 py-3 bg-[#222] text-gray-300 font-medium rounded-lg
                  border border-[#333] hover:bg-[#2a2a2a] transition-colors
                  disabled:opacity-50"
              >
                Back
              </button>
            )}

            {step < 3 ? (
              <button
                onClick={nextStep}
                className="flex-1 py-3 bg-[#60ffd0] text-black font-semibold rounded-lg
                  hover:bg-[#4de0b8] transition-colors"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleCreateAirline}
                disabled={loading}
                className="flex-1 py-3 bg-[#60ffd0] text-black font-semibold rounded-lg
                  hover:bg-[#4de0b8] transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Launch Airline'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Recovery Link */}
        <div className="text-center mt-6">
          <button
            onClick={tryRecoverAirline}
            className="text-[#60ffd0] hover:text-[#4de0b8] text-sm"
          >
            Already have an airline? Click here to recover
          </button>
        </div>
      </div>
    </div>
  )
}
